import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, ButtonGroup, Button, Spinner } from 'reactstrap';
import { FaChartLine, FaBoxOpen, FaCalendarAlt, FaMoneyBillWave, FaFileExcel } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saleActions } from '../../sagas/saleSlice';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';

const Dashboard = () => {
  const [dateRange, setDateRange] = useState('today');
  const role = useSelector((state) => state.auth.role);

  const dispatch = useDispatch();
  const {
    totalSales,
    stockValue,
    lowStockItems,
    isLoading,
    totalRefunds,
    totalPurchases,
  } = useSelector(state => state.sales);

  useEffect(() => {
    dispatch(saleActions.getSalesData({ dateRange }));
  }, [dispatch, dateRange]);

  console.log(totalSales)
  const { t } = useTranslation();

  const handleExportSalesReport = async () => {
    try {
      Swal.fire({
        title: t('dashboard.exportLoading'),
        text: t('dashboard.exportLoadingText'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      dispatch(saleActions.getSalesReport({
        dateRange,
        onSuccess: (data) => {
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(data.map(sale => ({
            "Désignation": sale.productDescription,
            "Quantité": sale.quantity,
            "Total": sale.total
          })));

          XLSX.utils.book_append_sheet(wb, ws, `Sales_${dateRange}`);
          XLSX.writeFile(wb, `sales_report_${dateRange}.xlsx`);

          Swal.fire({
            icon: 'success',
            title: t('dashboard.exportSuccess'),
            text: t('dashboard.exportSuccessText'),
            timer: 2000,
            showConfirmButton: false
          });
        }
      }));
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('dashboard.exportError'),
        text: error.message,
        confirmButtonColor: '#3085d6'
      });
    }
  };

  return (
    <Container fluid>
      {/* Date Range Selector */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaCalendarAlt className="text-primary me-2" />
                {t('dashboard.reports')}
              </h4>
              {role && role === 'ROLE_ADMIN' && <ButtonGroup>
                <Button
                  color={dateRange === 'today' ? 'primary' : 'light'}
                  onClick={() => {
                    setDateRange('today');
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.today')
                  )}
                </Button>
                <Button
                  color={dateRange === 'week' ? 'primary' : 'light'}
                  onClick={() => setDateRange('week')}
                  disabled={isLoading}

                >{isLoading ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  t('dashboard.week')
                )}
                </Button>
                <Button
                  color={dateRange === 'month' ? 'primary' : 'light'}
                  onClick={() => setDateRange('month')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (t('dashboard.month'))}
                </Button>
                <Button
                  color={dateRange === 'year' ? 'primary' : 'light'}
                  onClick={() => setDateRange('year')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (t('dashboard.year'))}
                </Button>
                <Button
                  color="success"
                  className="ms-2"
                  onClick={handleExportSalesReport}
                  disabled={isLoading}
                >
                  <FaFileExcel className="me-2" />
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.exportSales')
                  )}
                </Button>
              </ButtonGroup>}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Sales Summary Cards */}
      {role && role === 'ROLE_ADMIN' && (<Row className="mb-4">
        <Col md="4">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaChartLine className="text-primary me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalSales')}</h5>
              </div>
              <h3 className="mb-0">{totalSales.toLocaleString()} DH</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaMoneyBillWave className="text-success me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalPurchase')}</h5>
              </div>
              <h3 className="mb-0">{totalPurchases.toLocaleString()} DH</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="shadow-sm h-100">
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaMoneyBillWave className="text-success me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.stockValue')}</h5>
              </div>
              <h3 className="mb-0">{stockValue.toLocaleString()} DH</h3>
            </CardBody>
          </Card>
        </Col>

      </Row>)
      }

      {/* Low Stock Alert */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <CardBody style={{ overflowY: 'auto', maxHeight: '450px' }}> {/* Adjust maxHeight as needed */}
              <div className="d-flex align-items-center mb-3">
                <FaBoxOpen className="text-warning me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.lowStockAlert')}</h5>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>{t('product.code')}</th>
                      <th>{t('product.description')}</th>
                      <th>{t('product.stockQty')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.code}</td>
                        <td>{item.name}</td>
                        <td className="text-danger fw-bold">{item.stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container >
  );
};

export default Dashboard;